import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Eye, Save, ExternalLink, Search,
  Heading1, Pilcrow, Image as ImageIcon, MousePointerClick, Minus, MoveVertical,
  Layout, Columns, LayoutGrid, Film, Code, Grid3x3, List, Quote, Loader2, ChevronDown, ChevronRight,
  FileText, Globe, Settings as SettingsIcon, Tag, Upload, Smartphone, Monitor, Check, AlertCircle,
  HelpCircle, Link2, MapPin, Navigation, Database, GripVertical,
  Megaphone, BarChart3, MessageSquare, Sparkles, Layers, Star, Award, Zap,
} from "lucide-react";

const API = `${import.meta.env.BASE_URL}api`;
const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "https://www.vertexclimate.co.uk";

type Block = any;
type Page = {
  id: number; title: string; slug: string; parentId: number | null; status: "draft" | "published";
  blocks: Block[]; seoTitle: string | null; metaDescription: string | null;
  focusKeyphrase: string | null; keywords: string[] | null;
  featuredImageUrl: string | null; ogImageUrl: string | null;
  canonicalUrl: string | null; noindex: boolean; showInNav: boolean; navOrder: number;
  publishedAt: string | null; createdAt: string; updatedAt: string;
};

const newId = () => Math.random().toString(36).slice(2, 10);

function pickImageFile(): Promise<File | null> {
  return new Promise(resolve => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = () => resolve(input.files?.[0] || null);
    input.click();
  });
}

async function compressImage(file: File, maxW = 1600, quality = 0.85): Promise<{ blob: Blob; mime: string }> {
  if (file.type === "image/gif") return { blob: file, mime: "image/gif" };
  const img = new Image();
  const url = URL.createObjectURL(file);
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
  const ratio = Math.min(1, maxW / img.naturalWidth);
  const w = Math.round(img.naturalWidth * ratio);
  const h = Math.round(img.naturalHeight * ratio);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(url);
  const hasAlpha = file.type === "image/png" || file.type === "image/webp";
  const mime = hasAlpha ? "image/webp" : "image/jpeg";
  return new Promise(res => c.toBlob(b => res({ blob: b!, mime }), mime, quality));
}

async function uploadImageFile(file: File): Promise<{ url: string }> {
  const { blob, mime } = await compressImage(file);
  const b64 = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] || ""); r.onerror = rej; r.readAsDataURL(blob); });
  const res = await fetch(`${API}/admin/newsletter/images`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ fileName: file.name, mimeType: mime, data: b64 }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || "Upload failed");
  return res.json();
}

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 200);

export default function AdminPages() {
  const [list, setList] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/pages`, { credentials: "include" });
      if (r.ok) setList(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchList(); }, []);

  const createPage = async () => {
    setCreating(true);
    try {
      const r = await fetch(`${API}/admin/pages`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "Untitled Page", blocks: [] }),
      });
      const p = await r.json();
      await fetchList();
      setEditingId(p.id);
    } finally { setCreating(false); }
  };

  const deletePage = async (id: number) => {
    if (!confirm("Delete this page? This cannot be undone.")) return;
    await fetch(`${API}/admin/pages/${id}`, { method: "DELETE", credentials: "include" });
    fetchList();
  };

  if (editingId != null) {
    return <PageEditor id={editingId} allPages={list} onClose={() => { setEditingId(null); fetchList(); }} />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pages</h1>
          <p className="text-sm text-slate-500 mt-1">Build and manage your website pages with full SEO control.</p>
        </div>
        <button onClick={createPage} disabled={creating} className="bg-primary text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 disabled:opacity-50">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} New Page
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No pages yet. Create your first one.</p>
          <button onClick={createPage} className="bg-primary text-white px-4 py-2 rounded-lg font-medium inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create Page</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Title</th>
                <th className="px-4 py-3 font-semibold">URL</th>
                <th className="px-4 py-3 font-semibold">Parent</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3 font-semibold w-32"></th>
              </tr>
            </thead>
            <tbody>
              {list.map(p => {
                const parent = list.find(x => x.id === p.parentId);
                return (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-900">{p.title}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">/{p.slug}</td>
                    <td className="px-4 py-3 text-slate-500">{parent?.title || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.updatedAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        {p.status === "published" && <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="View live"><ExternalLink className="w-4 h-4" /></a>}
                        <button onClick={() => setEditingId(p.id)} className="px-3 py-1.5 text-xs rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium">Edit</button>
                        <button onClick={() => deletePage(p.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PageEditor({ id, allPages, onClose }: { id: number; allPages: Page[]; onClose: () => void }) {
  const [page, setPage] = useState<Page | null>(null);
  const [tab, setTab] = useState<"content" | "seo" | "schema" | "settings">("content");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/pages/${id}`, { credentials: "include" }).then(r => r.json()).then(setPage);
  }, [id]);

  const update = (patch: Partial<Page>) => setPage(p => p ? { ...p, ...patch } : p);

  const save = async (status?: "draft" | "published") => {
    if (!page) return;
    setSaving(true);
    try {
      const body = { ...page, status: status || page.status };
      const r = await fetch(`${API}/admin/pages/${id}`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        const updated = await r.json();
        setPage(updated);
        setSavedAt(new Date());
      } else {
        const err = await r.json().catch(() => ({}));
        alert(err.error || "Save failed");
      }
    } finally { setSaving(false); }
  };

  if (!page) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const parentOptions = allPages.filter(p => p.id !== id);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={onClose} className="text-sm text-slate-500 hover:text-slate-900 font-medium">← Back to Pages</button>
          <div className="flex-1 mx-4">
            <input value={page.title} onChange={e => update({ title: e.target.value })} className="w-full text-lg font-semibold text-slate-900 bg-transparent border-0 focus:outline-none focus:bg-slate-50 rounded px-2 py-1" placeholder="Page title" />
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${page.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{page.status}</span>
          {savedAt && <span className="text-xs text-slate-400">Saved {savedAt.toLocaleTimeString()}</span>}
          <button onClick={() => setShowPreview(true)} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Eye className="w-4 h-4" /> Preview</button>
          <button onClick={() => save("draft")} disabled={saving} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Draft
          </button>
          <button onClick={() => save("published")} disabled={saving} className="px-4 py-1.5 text-sm rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 disabled:opacity-50">
            {page.status === "published" ? "Update" : "Publish"}
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1 border-t border-slate-100">
          {([
            { k: "content", l: "Content", i: Layout },
            { k: "seo", l: "SEO", i: Search },
            { k: "schema", l: "Schema", i: Database },
            { k: "settings", l: "Settings", i: SettingsIcon },
          ] as const).map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${tab === t.k ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
              <t.i className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {tab === "content" && <ContentBuilder page={page} onChange={update} />}
        {tab === "seo" && <SeoPanel page={page} onChange={update} />}
        {tab === "schema" && <SchemaPanel schemas={(page as any).schemas || []} onChange={s => update({ ...(page as any), schemas: s } as any)} />}
        {tab === "settings" && <SettingsPanel page={page} parentOptions={parentOptions} onChange={update} />}
      </div>

      {showPreview && <PreviewModal page={page} onClose={() => setShowPreview(false)} />}
    </div>
  );
}

const BLOCK_TYPES: { type: string; label: string; icon: any }[] = [
  { type: "hero", label: "Hero", icon: Layout },
  { type: "heading", label: "Heading", icon: Heading1 },
  { type: "paragraph", label: "Paragraph", icon: Pilcrow },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "cta", label: "CTA Banner", icon: Megaphone },
  { type: "stats", label: "Stats", icon: BarChart3 },
  { type: "featureGrid", label: "Feature Grid", icon: Sparkles },
  { type: "testimonial", label: "Testimonial", icon: MessageSquare },
  { type: "twoColumn", label: "Two Columns", icon: Columns },
  { type: "columns", label: "Columns", icon: LayoutGrid },
  { type: "video", label: "Video", icon: Film },
  { type: "gallery", label: "Gallery", icon: Grid3x3 },
  { type: "list", label: "List", icon: List },
  { type: "quote", label: "Quote", icon: Quote },
  { type: "faq", label: "FAQ", icon: HelpCircle },
  { type: "related", label: "Related Links", icon: Link2 },
  { type: "localBusiness", label: "Business Card", icon: MapPin },
  { type: "breadcrumb", label: "Breadcrumb", icon: Navigation },
  { type: "html", label: "HTML", icon: Code },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
];

const SECTIONS: { id: string; label: string; icon: any; description: string; preview?: string; build: () => Block[] }[] = [
  {
    id: "why-best-image", label: "Why We're The Best", icon: Award, preview: "/sections/why-choose-us.png",
    description: "Image left, dark blue panel with checks + CTA",
    build: () => [
      { id: newId(), type: "twoColumn", gap: 0, background: "none", fullWidth: true, leftVAlign: "center", leftAlign: "center", rightVAlign: "center", rightAlign: "left",
        leftHtml: "", rightHtml: "",
        leftBlocks: [{ id: newId(), type: "image", url: "/sections/why-choose-us.png", alt: "Certified AC engineer at work in {{city.title}}", align: "center" }],
        rightBlocks: [
          { id: newId(), type: "twoColumn", gap: 0, background: "dark", leftVAlign: "center", leftAlign: "left", rightVAlign: "center", rightAlign: "left",
            leftHtml: "", rightHtml: "",
            leftBlocks: [
              { id: newId(), type: "heading", text: "Why we're the best for {{service.title}}", level: 2, align: "left" },
              { id: newId(), type: "paragraph", html: "Our commitment to quality of services and customer satisfaction sets us apart every time.", align: "left" },
              { id: newId(), type: "list", style: "check", columns: 2, items: ["Certified mechanics", "Quick turnaround", "Affordable pricing", "Quality parts", "Quick response"] },
              { id: newId(), type: "button", label: "About us", href: "/about", style: "secondary", align: "left" },
            ],
            rightBlocks: [],
          },
        ],
      },
    ],
  },
  {
    id: "craft-story", label: "The Craft Story", icon: Sparkles, preview: "/sections/craft-story.png",
    description: "Blue background, image + craftsmanship copy + checks",
    build: () => [
      { id: newId(), type: "twoColumn", gap: 48, background: "primary", fullWidth: true, leftVAlign: "center", leftAlign: "center", rightVAlign: "center", rightAlign: "left",
        leftHtml: "", rightHtml: "",
        leftBlocks: [{ id: newId(), type: "image", url: "/sections/craft-story.png", alt: "Vertex Climate engineer installing AC in {{city.title}}", align: "center" }],
        rightBlocks: [
          { id: newId(), type: "heading", text: "The craft of climate control, done right.", level: 2, align: "left" },
          { id: newId(), type: "paragraph", html: "At Vertex Climate, we don't just bolt units to walls. We engineer environments. Based in {{city.title}}, we understand the unique challenges of both historic residential properties and modern commercial spaces.", align: "left" },
          { id: newId(), type: "paragraph", html: "Our approach is simple: immaculate workmanship, transparent communication, and an obsessive attention to detail. When you hire us, you're not just getting air conditioning — you're getting peace of mind.", align: "left" },
          { id: newId(), type: "list", style: "check", columns: 1, items: [
            "Free, no-obligation site surveys and exact quotes",
            "Respectful engineers who protect your property",
            "Partnerships with tier-1 manufacturers",
            "Comprehensive warranties on all installations",
          ]},
        ],
      },
    ],
  },
  {
    id: "innovative-features", label: "Innovative Features", icon: Zap, preview: "/sections/features-4col.png",
    description: "Eyebrow + heading + 4-column feature grid",
    build: () => [
      { id: newId(), type: "paragraph", html: "<span style=\"color:#dc2626;font-weight:700;letter-spacing:0.05em\">// Our Features</span>", align: "center" },
      { id: newId(), type: "heading", text: "Elevating your air condition with our innovative features.", level: 2, align: "center" },
      { id: newId(), type: "featureGrid", columns: 4, items: [
        { icon: "🧹", title: "Dust Cleaning", description: "At Vertex Climate, we understand the importance of maintaining clean air in your home or business." },
        { icon: "🤫", title: "Quiet Operation", description: "We believe that comfort shouldn't come with noise. Our advanced HVAC systems run whisper-quiet." },
        { icon: "⚡", title: "Efficient Solutions", description: "Providing energy-efficient HVAC systems that deliver optimal performance while reducing bills." },
        { icon: "🌬️", title: "Fresh Air Flow", description: "Continuous fresh airflow keeps every room comfortable and air quality consistently high." },
      ]},
    ],
  },
  {
    id: "about-us", label: "About (simple)", icon: Award, description: "Two-column with image + story",
    build: () => [
      { id: newId(), type: "heading", text: "About VertexAir", level: 2, align: "center" },
      { id: newId(), type: "twoColumn", gap: 40, leftVAlign: "center", leftAlign: "left", rightVAlign: "top", rightAlign: "left",
        leftHtml: "<p>For over 15 years, VertexAir has been the trusted name in {{city.title}} for professional air conditioning and HVAC services. Our certified engineers deliver reliable installations, rapid repairs, and ongoing maintenance — backed by a 12-month workmanship guarantee.</p><p>From homes to commercial sites, we treat every job with the same standard of care.</p>",
        rightHtml: "", leftBlocks: [], rightBlocks: [{ id: newId(), type: "image", url: "", alt: "Our team at work", align: "center" }],
      },
    ],
  },
  {
    id: "our-services", label: "Our Services", icon: Sparkles, description: "3-column feature grid of services",
    build: () => [
      { id: newId(), type: "heading", text: "Our {{service.title}} Services in {{city.title}}", level: 2, align: "center" },
      { id: newId(), type: "paragraph", html: "Everything you need for {{service.title}}, delivered by qualified local engineers.", align: "center" },
      { id: newId(), type: "featureGrid", columns: 3, items: [
        { icon: "⚡", title: "Fast Installation", description: "Same-day or next-day fitting across {{city.title}}." },
        { icon: "🔧", title: "Expert Repairs", description: "F-Gas certified engineers, 24/7 emergency response." },
        { icon: "🛡️", title: "Maintenance Plans", description: "Annual servicing to keep your system running efficiently." },
      ]},
    ],
  },
  {
    id: "why-choose-us", label: "Why Choose Us", icon: Star, description: "Stat strip + bullet reasons",
    build: () => [
      { id: newId(), type: "heading", text: "Why Choose VertexAir in {{city.title}}?", level: 2, align: "center" },
      { id: newId(), type: "stats", items: [
        { value: "15+", label: "Years Experience" },
        { value: "5,000+", label: "Installations" },
        { value: "24/7", label: "Emergency Support" },
        { value: "5.0★", label: "Customer Rating" },
      ]},
      { id: newId(), type: "list", ordered: false, items: [
        "Fully accredited F-Gas engineers based near {{city.title}}",
        "Same-day & next-day appointments across {{city.region}}",
        "Free no-obligation quotes for homes and businesses",
        "12-month workmanship guarantee on every install",
      ]},
    ],
  },
  {
    id: "testimonials", label: "Testimonials", icon: MessageSquare, description: "Customer reviews row",
    build: () => [
      { id: newId(), type: "heading", text: "What {{city.title}} Customers Say", level: 2, align: "center" },
      { id: newId(), type: "columns", columns: 3, gap: 24, items: [
        { html: "", blocks: [{ id: newId(), type: "testimonial", text: "Brilliant service from start to finish. Fitted our AC in a single day.", author: "Sarah M.", role: "Homeowner, {{city.title}}", rating: 5 }] },
        { html: "", blocks: [{ id: newId(), type: "testimonial", text: "Quick, professional, and great value. Highly recommended.", author: "James T.", role: "Office Manager", rating: 5 }] },
        { html: "", blocks: [{ id: newId(), type: "testimonial", text: "Called them at 8am, system was running by lunchtime.", author: "Priya K.", role: "Restaurant Owner", rating: 5 }] },
      ]},
    ],
  },
  {
    id: "contact-cta", label: "Contact CTA", icon: Zap, description: "Full-width call-to-action banner",
    build: () => [
      { id: newId(), type: "cta", title: "Ready for {{service.title}} in {{city.title}}?",
        subtitle: "Get a free, no-obligation quote within 24 hours. Talk to a local engineer today.",
        primaryLabel: "Get Free Quote", primaryHref: "/quote",
        secondaryLabel: "Call 020 8087 0906", secondaryHref: "tel:02080870906",
        background: "primary",
      },
    ],
  },
];

function newBlock(type: string): Block {
  const id = newId();
  switch (type) {
    case "hero": return { id, type, title: "Welcome", subtitle: "Your subtitle here", backgroundUrl: "", ctaLabel: "Get Started", ctaHref: "/", align: "center", overlay: 0.45 };
    case "heading": return { id, type, text: "Section Heading", level: 2, align: "left" };
    case "paragraph": return { id, type, html: "Write your content here. You can use <b>bold</b>, <i>italic</i>, or <a href=\"#\">links</a>.", align: "left" };
    case "image": return { id, type, url: "", alt: "", align: "center" };
    case "button": return { id, type, label: "Click Me", href: "/", align: "left", style: "primary" };
    case "twoColumn": return { id, type, leftHtml: "<p>Left column content.</p>", rightHtml: "<p>Right column content.</p>", leftBlocks: [], rightBlocks: [], gap: 32 };
    case "columns": return { id, type, columns: 3, gap: 24, items: [
      { html: "<h4>Column 1</h4><p>Content for column one.</p>", blocks: [] },
      { html: "<h4>Column 2</h4><p>Content for column two.</p>", blocks: [] },
      { html: "<h4>Column 3</h4><p>Content for column three.</p>", blocks: [] },
    ] };
    case "video": return { id, type, url: "https://www.youtube.com/watch?v=", caption: "" };
    case "gallery": return { id, type, images: [], columns: 3 };
    case "list": return { id, type, ordered: false, items: ["First item", "Second item", "Third item"] };
    case "quote": return { id, type, text: "An inspiring quote.", author: "" };
    case "html": return { id, type, html: "<div>Custom HTML</div>" };
    case "faq": return { id, type, items: [{ question: "How long does installation take?", answer: "A typical installation takes 4–8 hours." }, { question: "Do you offer warranty?", answer: "Yes, 5 years on parts and labour." }] };
    case "related": return { id, type, label: "Related Services", collection: "", pattern: "/{{item.slug}}", limit: 8, columns: 3 };
    case "localBusiness": return { id, type, name: "VertexAir Climate Systems", description: "Professional AC & HVAC services.", telephone: "", email: "", image: "", address: { streetAddress: "", addressLocality: "", postalCode: "" }, openingHours: ["Mon-Fri 08:00-18:00"] };
    case "breadcrumb": return { id, type, items: [{ name: "Home", url: "/" }, { name: "{{page.title}}", url: "" }] };
    case "cta": return { id, type, title: "Ready to get started?", subtitle: "Get a free quote within 24 hours.", primaryLabel: "Get Free Quote", primaryHref: "/quote", secondaryLabel: "Call us", secondaryHref: "tel:02080870906", background: "primary" };
    case "stats": return { id, type, items: [
      { value: "15+", label: "Years Experience" },
      { value: "5,000+", label: "Installations" },
      { value: "24/7", label: "Emergency Support" },
      { value: "5.0★", label: "Customer Rating" },
    ] };
    case "featureGrid": return { id, type, columns: 3, items: [
      { icon: "⚡", title: "Fast", description: "Same-day appointments available." },
      { icon: "🔧", title: "Expert", description: "F-Gas certified engineers." },
      { icon: "🛡️", title: "Guaranteed", description: "12-month workmanship warranty." },
    ] };
    case "testimonial": return { id, type, text: "Brilliant service from start to finish.", author: "Customer Name", role: "Homeowner", rating: 5, avatarUrl: "" };
    case "divider": return { id, type };
    case "spacer": return { id, type, height: 32 };
    default: return { id, type };
  }
}

// Pointer-events drag-and-drop reorder hook. Far more reliable than HTML5
// native DnD inside nested iframes (Replit workspace) and works on touch.
//
// How it works:
//  - onPointerDown records the source index, pointer id, and start position
//  - We wait for ~6px of cursor movement before "arming" the drag, so plain
//    clicks/taps inside the card still work normally
//  - On every pointermove we hit-test with document.elementFromPoint and walk
//    up to the closest card with a matching `data-card-kind`. The cursor's
//    Y position vs the card midpoint decides whether the drop lands above
//    (gapIdx=i) or below (gapIdx=i+1)
//  - On pointerup we commit, on pointercancel/Escape we abort.
//
// `cardKind` scopes hit-testing so a nested-block drag never accidentally
// targets a top-level card (and vice versa).
function useBlockReorder(
  cardKind: string,
  onReorder: (from: number, gapIdx: number) => void,
) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  // Mirror state in a ref so the window-level pointermove/up handlers (bound
  // once) can read the latest values without re-binding on every render.
  const overIdxRef = useRef<number | null>(null);
  overIdxRef.current = overIdx;
  const cbRef = useRef(onReorder);
  cbRef.current = onReorder;
  const startRef = useRef<{ idx: number; pointerId: number; x: number; y: number; armed: boolean } | null>(null);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const s = startRef.current; if (!s || e.pointerId !== s.pointerId) return;
      if (!s.armed) {
        if (Math.abs(e.clientX - s.x) + Math.abs(e.clientY - s.y) < 6) return;
        s.armed = true;
        setDragIdx(s.idx);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
      }
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const card = el?.closest(`[data-card-kind="${cardKind}"]`) as HTMLElement | null;
      if (!card) {
        // Pointer is outside any valid target — clear overIdx so a release
        // here doesn't commit to a stale position.
        if (overIdxRef.current !== null) setOverIdx(null);
        return;
      }
      const idx = Number(card.dataset.idx);
      if (Number.isNaN(idx)) return;
      const rect = card.getBoundingClientRect();
      const next = e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
      if (overIdxRef.current !== next) setOverIdx(next);
    };
    const finish = (commit: boolean) => {
      const s = startRef.current;
      // Always restore body styles even if no active drag, to recover from any
      // unexpected state.
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (!s) { setDragIdx(null); setOverIdx(null); return; }
      const armed = s.armed;
      const from = s.idx;
      const to = overIdxRef.current;
      startRef.current = null;
      setDragIdx(null); setOverIdx(null);
      if (commit && armed && to != null) cbRef.current(from, to);
    };
    const up = (e: PointerEvent) => {
      const s = startRef.current; if (!s || e.pointerId !== s.pointerId) return;
      finish(true);
    };
    const cancel = (e: PointerEvent) => {
      const s = startRef.current; if (!s || e.pointerId !== s.pointerId) return;
      finish(false);
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") finish(false); };
    // Fail-safes: cancel any in-flight drag if the window loses focus, the
    // tab is hidden, or the user switches away. Without these, a missed
    // pointerup (pointer leaving the browser edge) would leave the drag stuck.
    const cancelIfActive = () => { if (startRef.current) finish(false); };
    const onVisibility = () => { if (document.visibilityState !== "visible") cancelIfActive(); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", key);
    window.addEventListener("blur", cancelIfActive);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      // Ensure any active drag is torn down before the listeners go away, so
      // body styles don't end up stuck if the component unmounts mid-drag.
      if (startRef.current) finish(false);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key);
      window.removeEventListener("blur", cancelIfActive);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cardKind]);

  const cardPointerProps = (i: number) => ({
    "data-card-kind": cardKind,
    "data-idx": i,
    onPointerDown: (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement;
      // Don't hijack clicks inside form controls / buttons / links / rich-text
      if (t.closest('input, textarea, select, button, a, [contenteditable="true"], .ProseMirror, [data-no-drag]')) return;
      // Capture the pointer on the card itself so we still get pointerup even
      // if the cursor briefly leaves the document (e.g. window edge).
      try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch {}
      startRef.current = { idx: i, pointerId: e.pointerId, x: e.clientX, y: e.clientY, armed: false };
    },
  } as React.HTMLAttributes<HTMLDivElement> & { "data-card-kind": string; "data-idx": number });

  return { dragIdx, overIdx, cardPointerProps };
}

export function ContentBuilder({ page, onChange }: { page: Page; onChange: (p: Partial<Page>) => void }) {
  const blocks = page.blocks || [];
  const setBlocks = (b: Block[]) => onChange({ blocks: b });
  const addBlock = (type: string) => setBlocks([...blocks, newBlock(type)]);
  const insertSection = (id: string) => {
    const s = SECTIONS.find(x => x.id === id); if (!s) return;
    setBlocks([...blocks, ...s.build()]);
  };
  const updateBlock = (i: number, patch: Block) => setBlocks(blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const removeBlock = (i: number) => setBlocks(blocks.filter((_, idx) => idx !== i));
  const moveBlock = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= blocks.length) return;
    const next = [...blocks]; [next[i], next[j]] = [next[j], next[i]]; setBlocks(next);
  };
  // Section-from-sidebar drag still uses HTML5 DnD (needs copy effect + a real
  // drag image). Block reorder uses POINTER events — far more reliable across
  // nested iframes (Replit workspace preview) and touch devices.
  const sectionDragIdRef = useRef<string | null>(null);
  const [sectionDragging, setSectionDragging] = useState<string | null>(null);
  const [sectionOverIdx, setSectionOverIdx] = useState<number | null>(null);
  // Parallel channel for dragging a single block type from the sidebar Blocks
  // tab. Same UX as section drag, but inserts one new block of `type` at the
  // chosen gap index.
  const blockTypeDragRef = useRef<string | null>(null);
  const [blockTypeDragging, setBlockTypeDragging] = useState<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<"blocks" | "sections">("blocks");

  const moveBlockTo = (from: number, gapIdx: number) => {
    if (from < 0 || from >= blocks.length) return;
    const next = [...blocks];
    const [moved] = next.splice(from, 1);
    const target = gapIdx > from ? gapIdx - 1 : gapIdx;
    if (target === from) return;
    next.splice(target, 0, moved);
    setBlocks(next);
  };
  const insertSectionAt = (sectionId: string, idx: number) => {
    const s = SECTIONS.find(x => x.id === sectionId); if (!s) return;
    const built = s.build();
    const clamped = Math.max(0, Math.min(idx, blocks.length));
    const next = [...blocks]; next.splice(clamped, 0, ...built); setBlocks(next);
  };
  const acceptSectionDrop = (e: React.DragEvent) => {
    return sectionDragIdRef.current || e.dataTransfer.getData("application/x-section-id") || null;
  };
  const acceptBlockTypeDrop = (e: React.DragEvent) => {
    return blockTypeDragRef.current || e.dataTransfer.getData("application/x-block-type") || null;
  };
  const insertBlockAt = (type: string, idx: number) => {
    const clamped = Math.max(0, Math.min(idx, blocks.length));
    const next = [...blocks]; next.splice(clamped, 0, newBlock(type)); setBlocks(next);
  };
  // Single helper used by every drop site: figure out which sidebar item was
  // dragged (a section or a single block type) and insert it at the gap.
  const acceptSidebarDrop = (e: React.DragEvent, gapIdx: number) => {
    const sectionId = acceptSectionDrop(e);
    const blockType = acceptBlockTypeDrop(e);
    sectionDragIdRef.current = null; setSectionDragging(null); setSectionOverIdx(null);
    blockTypeDragRef.current = null; setBlockTypeDragging(null);
    if (sectionId) { insertSectionAt(sectionId, gapIdx); return true; }
    if (blockType) { insertBlockAt(blockType, gapIdx); return true; }
    return false;
  };
  const isSidebarDragEvent = (e: React.DragEvent) =>
    !!sectionDragIdRef.current
    || !!blockTypeDragRef.current
    || e.dataTransfer.types.includes("application/x-section-id")
    || e.dataTransfer.types.includes("application/x-block-type");

  // Pointer-events drag for block reorder. We track the source index on
  // pointerdown, wait for a small movement threshold to arm the drag (so plain
  // clicks still work), then hit-test the card under the cursor on every move.
  const { dragIdx, overIdx, cardPointerProps } = useBlockReorder(
    "top-block-card",
    (from, gapIdx) => moveBlockTo(from, gapIdx),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
      <div className="space-y-3">
        {blocks.length === 0 && !sectionDragging && !blockTypeDragging && (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-slate-200">
            <Layout className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Start by adding a block or a ready-made section from the right →</p>
          </div>
        )}
        {blocks.length === 0 && (sectionDragging || blockTypeDragging) && (
          <div
            onDragOver={e => { e.preventDefault(); try { e.dataTransfer.dropEffect = "copy"; } catch {} setSectionOverIdx(0); }}
            onDragLeave={() => { if (sectionOverIdx === 0) setSectionOverIdx(null); }}
            onDrop={e => { e.preventDefault(); acceptSidebarDrop(e, 0); }}
            className={`rounded-2xl border-2 border-dashed transition flex items-center justify-center text-sm font-semibold py-20 ${sectionOverIdx === 0 ? "border-primary bg-primary/10 text-primary" : "border-primary/40 bg-primary/5 text-primary/80"}`}
          >
            Drop {blockTypeDragging ? "block" : "section"} here
          </div>
        )}
        {blocks.map((b, i) => {
          // Pick whichever drag is active: block reorder OR sidebar drag (section/block).
          const sidebarActive = !!sectionDragging || !!blockTypeDragging;
          const activeOverIdx = sidebarActive ? sectionOverIdx : overIdx;
          const anyDrag = dragIdx != null || sidebarActive;
          const showTop = anyDrag && activeOverIdx === i;
          const showBottom = anyDrag && activeOverIdx === i + 1;
          return (
            <div
              key={b.id || i}
              {...cardPointerProps(i)}
              onDragOver={e => {
                // Cards are HTML5 drop targets ONLY for sidebar drags (section or
                // single block type). Block-to-block reorder is handled by pointer
                // events above.
                if (!isSidebarDragEvent(e)) return;
                e.preventDefault();
                try { e.dataTransfer.dropEffect = "copy"; } catch {}
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setSectionOverIdx(e.clientY < rect.top + rect.height / 2 ? i : i + 1);
              }}
              onDragLeave={e => {
                const related = e.relatedTarget as Node | null;
                if (!related || !(e.currentTarget as HTMLElement).contains(related)) setSectionOverIdx(null);
              }}
              onDrop={e => {
                e.preventDefault();
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const gapIdx = e.clientY < rect.top + rect.height / 2 ? i : i + 1;
                acceptSidebarDrop(e, gapIdx);
              }}
              className={`relative bg-white rounded-xl border p-4 group transition border-slate-200 hover:border-primary/40 select-none ${dragIdx === i ? "opacity-50" : ""}`}>
              {showTop && <div className="absolute -top-1.5 left-0 right-0 h-1 bg-primary rounded-full shadow-[0_0_0_3px_rgba(37,99,235,0.25)] pointer-events-none z-10" />}
              {showBottom && <div className="absolute -bottom-1.5 left-0 right-0 h-1 bg-primary rounded-full shadow-[0_0_0_3px_rgba(37,99,235,0.25)] pointer-events-none z-10" />}
              <div className="flex items-center justify-between mb-3">
                <span
                  title="Drag to reorder"
                  className="text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-grab active:cursor-grabbing flex items-center gap-1 select-none px-1 py-0.5 rounded hover:bg-slate-100"
                >
                  <GripVertical className="w-3.5 h-3.5 text-slate-400" /> {b.type}
                </span>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
                  <button onClick={() => moveBlock(i, -1)} disabled={i === 0} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => removeBlock(i)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <BlockEditor block={b} onChange={p => updateBlock(i, p)} />
            </div>
          );
        })}
      </div>

      <div className="lg:sticky lg:top-32 lg:self-start">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex border-b border-slate-200 text-xs font-semibold">
            <button onClick={() => setSidebarTab("blocks")} className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 ${sidebarTab === "blocks" ? "text-primary border-b-2 border-primary -mb-px bg-primary/5" : "text-slate-500 hover:text-slate-900"}`}>
              <Plus className="w-3.5 h-3.5" /> Blocks
            </button>
            <button onClick={() => setSidebarTab("sections")} className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 ${sidebarTab === "sections" ? "text-primary border-b-2 border-primary -mb-px bg-primary/5" : "text-slate-500 hover:text-slate-900"}`}>
              <Layers className="w-3.5 h-3.5" /> Sections
            </button>
          </div>
          <div className="p-4">
            {sidebarTab === "blocks" ? (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Click to append, or <strong>drag onto the canvas</strong> to drop at any position.</p>
                <div className="grid grid-cols-2 gap-2">
                  {BLOCK_TYPES.map(b => (
                    <div
                      key={b.type}
                      draggable
                      role="button"
                      tabIndex={0}
                      aria-label={`Insert block: ${b.label}. Click or press Enter to append, or drag onto the canvas to drop at a specific position.`}
                      onDragStart={e => {
                        blockTypeDragRef.current = b.type;
                        setBlockTypeDragging(b.type);
                        try {
                          e.dataTransfer.setData("application/x-block-type", b.type);
                          e.dataTransfer.effectAllowed = "copy";
                        } catch {}
                      }}
                      onDragEnd={() => { blockTypeDragRef.current = null; setBlockTypeDragging(null); setSectionOverIdx(null); }}
                      onClick={() => addBlock(b.type)}
                      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); addBlock(b.type); } }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border cursor-grab active:cursor-grabbing transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${blockTypeDragging === b.type ? "opacity-50 border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600 hover:border-primary hover:bg-primary/5 hover:text-primary"}`}
                    >
                      <b.icon className="w-4 h-4" />
                      <span className="text-xs font-medium">{b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">Click to append, or <strong>drag onto the canvas</strong> to drop at any position.</p>
                {SECTIONS.map(s => (
                  <div key={s.id}
                    draggable
                    role="button"
                    tabIndex={0}
                    aria-label={`Insert section: ${s.label}. Click or press Enter to append, or drag onto the canvas to drop at a specific position.`}
                    onDragStart={e => {
                      sectionDragIdRef.current = s.id;
                      setSectionDragging(s.id);
                      try {
                        e.dataTransfer.setData("application/x-section-id", s.id);
                        e.dataTransfer.effectAllowed = "copy";
                      } catch {}
                    }}
                    onDragEnd={() => { sectionDragIdRef.current = null; setSectionDragging(null); setSectionOverIdx(null); }}
                    onClick={() => insertSection(s.id)}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); insertSection(s.id); } }}
                    className={`w-full text-left p-3 rounded-lg border cursor-grab active:cursor-grabbing group transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${sectionDragging === s.id ? "opacity-50 border-primary" : "border-slate-200 hover:border-primary hover:bg-primary/5"}`}>
                    {s.preview && (
                      <div className="mb-2 aspect-[16/9] rounded-md overflow-hidden bg-slate-100 border border-slate-200">
                        <img src={s.preview} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                      <s.icon className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-slate-800 group-hover:text-primary">{s.label}</span>
                    </div>
                    <p className="text-xs text-slate-500">{s.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageInput({ value, onChange, placeholder = "Image URL or upload" }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [busy, setBusy] = useState(false);
  const onUpload = async () => {
    const f = await pickImageFile(); if (!f) return;
    setBusy(true);
    try { const r = await uploadImageFile(f); onChange(r.url); }
    catch (e: any) { alert("Upload failed: " + (e?.message || "unknown")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg" />
        <button type="button" onClick={onUpload} disabled={busy} className="px-3 py-2 text-sm rounded-lg bg-primary text-white disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
        </button>
      </div>
      {value && <img src={value} alt="" className="max-h-24 rounded border border-slate-200" />}
    </div>
  );
}

const FONT_OPTIONS = [
  { l: "Default", v: "" }, { l: "Arial", v: "Arial, sans-serif" }, { l: "Helvetica", v: "Helvetica, Arial, sans-serif" },
  { l: "Inter", v: "Inter, sans-serif" }, { l: "Georgia", v: "Georgia, serif" }, { l: "Times New Roman", v: "'Times New Roman', serif" },
  { l: "Verdana", v: "Verdana, sans-serif" }, { l: "Tahoma", v: "Tahoma, sans-serif" }, { l: "Courier New", v: "'Courier New', monospace" },
];

const NESTED_BLOCK_TYPES: { type: string; label: string; icon: any }[] = [
  { type: "heading", label: "Heading", icon: Heading1 },
  { type: "paragraph", label: "Paragraph", icon: Pilcrow },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "button", label: "Button", icon: MousePointerClick },
  { type: "cta", label: "CTA Banner", icon: Megaphone },
  { type: "stats", label: "Stats", icon: BarChart3 },
  { type: "featureGrid", label: "Feature Grid", icon: Sparkles },
  { type: "testimonial", label: "Testimonial", icon: MessageSquare },
  { type: "video", label: "Video", icon: Film },
  { type: "list", label: "List", icon: List },
  { type: "quote", label: "Quote", icon: Quote },
  { type: "html", label: "HTML", icon: Code },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: MoveVertical },
];

function NestedBlocksEditor({ blocks, onChange }: { blocks: Block[]; onChange: (b: Block[]) => void }) {
  const [picking, setPicking] = useState(false);
  const safe = Array.isArray(blocks) ? blocks : [];
  const add = (t: string) => { onChange([...safe, newBlock(t)]); setPicking(false); };
  const update = (i: number, patch: Block) => onChange(safe.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const remove = (i: number) => onChange(safe.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= safe.length) return;
    const next = [...safe]; [next[i], next[j]] = [next[j], next[i]]; onChange(next);
  };
  const moveTo = (from: number, gapIdx: number) => {
    if (from < 0 || from >= safe.length) return;
    const next = [...safe];
    const [moved] = next.splice(from, 1);
    const target = gapIdx > from ? gapIdx - 1 : gapIdx;
    if (target === from) return;
    next.splice(target, 0, moved);
    onChange(next);
  };
  // Pointer-events drag — same hook as the top-level builder, scoped by a
  // unique kind so hit-testing never crosses into top-level cards.
  const { dragIdx, overIdx, cardPointerProps } = useBlockReorder("nested-block-card", moveTo);
  return (
    <div className="space-y-2 pt-1">
      {safe.length > 0 && (
        <div className="space-y-2">
          {safe.map((b, i) => {
            const showTop = dragIdx != null && overIdx === i;
            const showBottom = dragIdx != null && overIdx === i + 1;
            return (
            <div
              key={b.id || i}
              {...cardPointerProps(i)}
              className={`relative bg-slate-50 border border-slate-200 rounded-lg p-2 select-none ${dragIdx === i ? "opacity-50" : ""}`}
            >
              {showTop && <div className="absolute -top-1 left-0 right-0 h-0.5 bg-primary rounded-full shadow-[0_0_0_2px_rgba(37,99,235,0.25)] pointer-events-none z-10" />}
              {showBottom && <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full shadow-[0_0_0_2px_rgba(37,99,235,0.25)] pointer-events-none z-10" />}
              <div className="flex items-center justify-between mb-2">
                <span title="Drag to reorder" className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 flex items-center gap-1 cursor-grab active:cursor-grabbing select-none">
                  <GripVertical className="w-3 h-3 text-slate-400" /> ↳ {b.type}
                </span>
                <div className="flex items-center gap-0.5">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-white disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === safe.length - 1} className="p-1 rounded hover:bg-white disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
                  <button onClick={() => remove(i)} className="p-1 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
              <BlockEditor block={b} onChange={p => update(i, p)} />
            </div>
            );
          })}
        </div>
      )}
      {picking ? (
        <div className="border border-primary/30 bg-primary/5 rounded-lg p-2">
          <div className="grid grid-cols-5 gap-1.5">
            {NESTED_BLOCK_TYPES.map(t => (
              <button key={t.type} onClick={() => add(t.type)} className="flex flex-col items-center gap-1 p-2 rounded hover:bg-white text-slate-600 hover:text-primary text-[10px]">
                <t.icon className="w-3.5 h-3.5" />{t.label}
              </button>
            ))}
          </div>
          <button onClick={() => setPicking(false)} className="text-[10px] text-slate-400 mt-1">Cancel</button>
        </div>
      ) : (
        <button onClick={() => setPicking(true)} className="w-full text-xs font-medium text-slate-500 hover:text-primary border border-dashed border-slate-300 hover:border-primary rounded-lg py-1.5 flex items-center justify-center gap-1">
          <Plus className="w-3 h-3" /> Add inside column
        </button>
      )}
    </div>
  );
}

function BlockEditor({ block, onChange }: { block: Block; onChange: (p: Block) => void }) {
  const inp = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg";
  switch (block.type) {
    case "hero":
      return (
        <div className="space-y-2">
          <input value={block.title} onChange={e => onChange({ title: e.target.value })} placeholder="Title" className={inp} />
          <input value={block.subtitle || ""} onChange={e => onChange({ subtitle: e.target.value })} placeholder="Subtitle (optional)" className={inp} />
          <ImageInput value={block.backgroundUrl || ""} onChange={v => onChange({ backgroundUrl: v })} placeholder="Background image" />
          <div className="grid grid-cols-2 gap-2">
            <input value={block.ctaLabel || ""} onChange={e => onChange({ ctaLabel: e.target.value })} placeholder="CTA label" className={inp} />
            <input value={block.ctaHref || ""} onChange={e => onChange({ ctaHref: e.target.value })} placeholder="CTA link" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={block.align || "center"} onChange={e => onChange({ align: e.target.value })} className={inp}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
            <label className="text-xs text-slate-500 flex items-center gap-2">Overlay: <input type="range" min={0} max={0.9} step={0.05} value={block.overlay ?? 0.45} onChange={e => onChange({ overlay: Number(e.target.value) })} className="flex-1" /></label>
          </div>
        </div>
      );
    case "heading":
      return (
        <div className="space-y-2">
          <input value={block.text} onChange={e => onChange({ text: e.target.value })} className={inp} />
          <div className="grid grid-cols-4 gap-2">
            <select value={block.level || 2} onChange={e => onChange({ level: Number(e.target.value) })} className={inp}><option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option><option value={4}>H4</option></select>
            <select value={block.align || "left"} onChange={e => onChange({ align: e.target.value })} className={inp}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
            <select value={block.fontFamily || ""} onChange={e => onChange({ fontFamily: e.target.value || undefined })} className={inp}>{FONT_OPTIONS.map(f => <option key={f.l} value={f.v}>{f.l}</option>)}</select>
            <input type="number" value={block.fontSize || ""} onChange={e => onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })} placeholder="Size px" className={inp} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Color:</label>
            <input type="color" value={block.color || "#0f172a"} onChange={e => onChange({ color: e.target.value })} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" />
            <input value={block.color || ""} onChange={e => onChange({ color: e.target.value || undefined })} placeholder="#0F2F4F or empty for default" className={inp} />
            {block.color && <button type="button" onClick={() => onChange({ color: undefined })} className="text-xs text-slate-500 hover:text-rose-500">Clear</button>}
          </div>
        </div>
      );
    case "paragraph":
      return (
        <div className="space-y-2">
          <RichTextarea value={block.html || ""} onChange={v => onChange({ html: v })} />
          <div className="grid grid-cols-3 gap-2">
            <select value={block.align || "left"} onChange={e => onChange({ align: e.target.value })} className={inp}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
            <select value={block.fontFamily || ""} onChange={e => onChange({ fontFamily: e.target.value || undefined })} className={inp}>{FONT_OPTIONS.map(f => <option key={f.l} value={f.v}>{f.l}</option>)}</select>
            <input type="number" value={block.fontSize || ""} onChange={e => onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })} placeholder="Size px" className={inp} />
          </div>
        </div>
      );
    case "image":
      return (
        <div className="space-y-2">
          <ImageInput value={block.url || ""} onChange={v => onChange({ url: v })} />
          <input value={block.alt || ""} onChange={e => onChange({ alt: e.target.value })} placeholder="Alt text (for SEO & accessibility)" className={inp} />
          <div className="grid grid-cols-3 gap-2">
            <input value={block.href || ""} onChange={e => onChange({ href: e.target.value })} placeholder="Link (optional)" className={inp} />
            <input type="number" value={block.width || ""} onChange={e => onChange({ width: e.target.value ? Number(e.target.value) : undefined })} placeholder="Width px" className={inp} />
            <select value={block.align || "center"} onChange={e => onChange({ align: e.target.value })} className={inp}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
          </div>
        </div>
      );
    case "button":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input value={block.label} onChange={e => onChange({ label: e.target.value })} placeholder="Label" className={inp} />
            <input value={block.href} onChange={e => onChange({ href: e.target.value })} placeholder="Link" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={block.style || "primary"} onChange={e => onChange({ style: e.target.value })} className={inp}><option value="primary">Primary</option><option value="secondary">Secondary</option><option value="outline">Outline</option></select>
            <select value={block.align || "left"} onChange={e => onChange({ align: e.target.value })} className={inp}><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
          </div>
        </div>
      );
    case "twoColumn":
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Background
              <select value={block.background || "none"} onChange={e => onChange({ background: e.target.value })} className={inp}>
                <option value="none">None</option>
                <option value="light">Light grey</option>
                <option value="primary">Primary (brand)</option>
                <option value="dark">Dark slate</option>
                <option value="gradient">Brand gradient</option>
              </select>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Column gap (px)
              <input type="number" value={block.gap ?? 32} onChange={e => onChange({ gap: Number(e.target.value) })} className={inp} />
            </label>
          </div>
          <label className="text-xs text-slate-500 flex flex-col gap-1">Background image (optional)
            <ImageInput value={block.backgroundUrl || ""} onChange={v => onChange({ backgroundUrl: v })} placeholder="URL or upload" />
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-600 select-none px-1">
            <input type="checkbox" checked={!!block.fullWidth} onChange={e => onChange({ fullWidth: e.target.checked })} className="w-4 h-4 rounded accent-primary" />
            <span><strong>Full-width section</strong> — break out of the page container and stretch edge-to-edge (best for hero/feature panels with colored backgrounds). When off, the section stays inside the normal content width.</span>
          </label>
          <div className="grid grid-cols-2 gap-2 px-1">
            <label className="text-xs text-slate-500 flex flex-col gap-1" title="Top + bottom inner spacing inside this section, in pixels. Leave blank to use the default for the chosen background.">
              Content space (top &amp; bottom, px)
              <div className="flex gap-1">
                <input type="number" min={0} step={4} value={typeof block.paddingY === "number" ? block.paddingY : ""} placeholder="auto" onChange={e => { const v = e.target.value; onChange({ paddingY: v === "" ? undefined : Number(v) }); }} className={inp} />
                {typeof block.paddingY === "number" && (
                  <button type="button" onClick={() => onChange({ paddingY: undefined })} className="px-2 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded" title="Reset to default">×</button>
                )}
              </div>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1" title="Left + right inner spacing inside this section, in pixels. Leave blank to use the default.">
              Content space (left &amp; right, px)
              <div className="flex gap-1">
                <input type="number" min={0} step={4} value={typeof block.paddingX === "number" ? block.paddingX : ""} placeholder="auto" onChange={e => { const v = e.target.value; onChange({ paddingX: v === "" ? undefined : Number(v) }); }} className={inp} />
                {typeof block.paddingX === "number" && (
                  <button type="button" onClick={() => onChange({ paddingX: undefined })} className="px-2 text-xs text-slate-500 hover:text-slate-900 border border-slate-200 rounded" title="Reset to default">×</button>
                )}
              </div>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 block">Left column</label>
              <RichTextarea value={block.leftHtml || ""} onChange={v => onChange({ leftHtml: v })} />
              <NestedBlocksEditor blocks={block.leftBlocks || []} onChange={(bs) => onChange({ leftBlocks: bs })} />
              <div className="grid grid-cols-2 gap-2">
                <select value={block.leftVAlign || "top"} onChange={e => onChange({ leftVAlign: e.target.value })} className={inp} title="Vertical alignment"><option value="top">Top</option><option value="center">Middle</option><option value="bottom">Bottom</option></select>
                <select value={block.leftAlign || "left"} onChange={e => onChange({ leftAlign: e.target.value })} className={inp} title="Horizontal alignment"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600 block">Right column</label>
              <RichTextarea value={block.rightHtml || ""} onChange={v => onChange({ rightHtml: v })} />
              <NestedBlocksEditor blocks={block.rightBlocks || []} onChange={(bs) => onChange({ rightBlocks: bs })} />
              <div className="grid grid-cols-2 gap-2">
                <select value={block.rightVAlign || "top"} onChange={e => onChange({ rightVAlign: e.target.value })} className={inp} title="Vertical alignment"><option value="top">Top</option><option value="center">Middle</option><option value="bottom">Bottom</option></select>
                <select value={block.rightAlign || "left"} onChange={e => onChange({ rightAlign: e.target.value })} className={inp} title="Horizontal alignment"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
              </div>
            </div>
          </div>
        </div>
      );
    case "columns": {
      const cols = Math.max(2, Math.min(6, Number(block.columns) || 3));
      const items: { html: string; blocks?: Block[] }[] = Array.isArray(block.items) ? block.items : [];
      const setItems = (next: { html: string; blocks?: Block[] }[]) => onChange({ items: next });
      const setCount = (n: number) => {
        const target = Math.max(2, Math.min(6, n));
        const next = [...items];
        while (next.length < target) next.push({ html: `<h4>Column ${next.length + 1}</h4><p>Content.</p>`, blocks: [] });
        while (next.length > target) next.pop();
        onChange({ columns: target, items: next });
      };
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex items-center gap-2">Columns:
              <select value={cols} onChange={e => setCount(Number(e.target.value))} className={inp}>
                {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-500 flex items-center gap-2">Gap:
              <input type="number" value={block.gap ?? 24} onChange={e => onChange({ gap: Number(e.target.value) })} className={inp} /> px
            </label>
          </div>
          <div className="space-y-3">
            {items.slice(0, cols).map((it: any, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2">
                <label className="text-xs font-semibold text-slate-600 block">Column {idx + 1}</label>
                <RichTextarea value={it.html || ""} onChange={v => setItems(items.map((x, i) => i === idx ? { ...x, html: v } : x))} />
                <NestedBlocksEditor
                  blocks={it.blocks || []}
                  onChange={(bs) => setItems(items.map((x, i) => i === idx ? { ...x, blocks: bs } : x))}
                />
                <div className="grid grid-cols-2 gap-2">
                  <select value={it.vAlign || "top"} onChange={e => setItems(items.map((x: any, i) => i === idx ? { ...x, vAlign: e.target.value } : x))} className={inp} title="Vertical alignment"><option value="top">Top</option><option value="center">Middle</option><option value="bottom">Bottom</option></select>
                  <select value={it.align || "left"} onChange={e => setItems(items.map((x: any, i) => i === idx ? { ...x, align: e.target.value } : x))} className={inp} title="Horizontal alignment"><option value="left">Left</option><option value="center">Center</option><option value="right">Right</option></select>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "video":
      return (
        <div className="space-y-2">
          <input value={block.url} onChange={e => onChange({ url: e.target.value })} placeholder="YouTube URL or video URL" className={inp} />
          <input value={block.caption || ""} onChange={e => onChange({ caption: e.target.value })} placeholder="Caption (optional)" className={inp} />
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {(block.images || []).map((img: any, idx: number) => (
              <div key={idx} className="relative group">
                <img src={img.url} alt={img.alt || ""} className="w-full aspect-square object-cover rounded-lg" />
                <button onClick={() => onChange({ images: block.images.filter((_: any, i: number) => i !== idx) })} className="absolute top-1 right-1 p-1 bg-white/90 rounded text-rose-500 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            <button onClick={async () => { const f = await pickImageFile(); if (!f) return; try { const r = await uploadImageFile(f); onChange({ images: [...(block.images || []), { url: r.url, alt: "" }] }); } catch (e: any) { alert("Upload failed: " + e?.message); } }}
              className="aspect-square border-2 border-dashed border-slate-200 rounded-lg hover:border-primary hover:bg-primary/5 flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-primary">
              <Plus className="w-5 h-5" /><span className="text-xs">Add Image</span>
            </button>
          </div>
          <label className="text-xs text-slate-500 flex items-center gap-2">Columns: <input type="number" min={1} max={6} value={block.columns || 3} onChange={e => onChange({ columns: Number(e.target.value) })} className="w-20 px-2 py-1 border border-slate-200 rounded" /></label>
        </div>
      );
    case "list":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Style
              <select value={block.style || (block.ordered ? "number" : "bullet")} onChange={e => onChange({ style: e.target.value, ordered: e.target.value === "number" })} className={inp}>
                <option value="bullet">• Bullet</option>
                <option value="number">1. Numbered</option>
                <option value="check">✓ Checkmark</option>
              </select>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Columns
              <select value={block.columns || 1} onChange={e => onChange({ columns: Number(e.target.value) })} className={inp}>
                <option value={1}>1 column</option>
                <option value={2}>2 columns</option>
                <option value={3}>3 columns</option>
              </select>
            </label>
          </div>
          {(block.items || []).map((it: string, idx: number) => (
            <div key={idx} className="flex gap-2">
              <input value={it} onChange={e => { const next = [...block.items]; next[idx] = e.target.value; onChange({ items: next }); }} className={inp} />
              <button onClick={() => onChange({ items: block.items.filter((_: any, i: number) => i !== idx) })} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...(block.items || []), "New item"] })} className="text-sm text-primary font-medium">+ Add item</button>
        </div>
      );
    case "quote":
      return (
        <div className="space-y-2">
          <textarea value={block.text} onChange={e => onChange({ text: e.target.value })} rows={3} className={inp} />
          <input value={block.author || ""} onChange={e => onChange({ author: e.target.value })} placeholder="Author (optional)" className={inp} />
        </div>
      );
    case "html":
      return <textarea value={block.html} onChange={e => onChange({ html: e.target.value })} rows={6} className={`${inp} font-mono text-xs`} placeholder="<div>Custom HTML</div>" />;
    case "faq":
      return (
        <div className="space-y-2">
          {(block.items || []).map((it: any, idx: number) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-2 space-y-2">
              <div className="flex items-center gap-2">
                <input value={it.question} onChange={e => { const next = [...block.items]; next[idx] = { ...it, question: e.target.value }; onChange({ items: next }); }} placeholder="Question" className={inp} />
                <button onClick={() => onChange({ items: block.items.filter((_: any, i: number) => i !== idx) })} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={it.answer} onChange={e => { const next = [...block.items]; next[idx] = { ...it, answer: e.target.value }; onChange({ items: next }); }} rows={2} placeholder="Answer (HTML allowed)" className={inp} />
            </div>
          ))}
          <button onClick={() => onChange({ items: [...(block.items || []), { question: "", answer: "" }] })} className="text-sm text-primary font-medium">+ Add Q&A</button>
        </div>
      );
    case "related":
      return (
        <div className="space-y-2">
          <input value={block.label || ""} onChange={e => onChange({ label: e.target.value })} placeholder="Section label (e.g. Other Cities We Serve)" className={inp} />
          <CollectionPicker value={block.collection || ""} onChange={v => onChange({ collection: v })} />
          <input value={block.pattern || ""} onChange={e => onChange({ pattern: e.target.value })} placeholder="Link pattern, e.g. /{{service.slug}}-in-{{item.slug}}" className={`${inp} font-mono text-xs`} />
          <p className="text-xs text-slate-400">Use <code>{`{{item.slug}}`}</code> for the linked entry. If this page is programmatic, you can also use the page's variables like <code>{`{{service.slug}}`}</code>.</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex items-center gap-2">Limit: <input type="number" value={block.limit || 12} onChange={e => onChange({ limit: Number(e.target.value) })} className={inp} /></label>
            <label className="text-xs text-slate-500 flex items-center gap-2">Cols: <input type="number" min={1} max={6} value={block.columns || 3} onChange={e => onChange({ columns: Number(e.target.value) })} className={inp} /></label>
          </div>
        </div>
      );
    case "localBusiness":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input value={block.name || ""} onChange={e => onChange({ name: e.target.value })} placeholder="Business name" className={inp} />
            <input value={block.telephone || ""} onChange={e => onChange({ telephone: e.target.value })} placeholder="Phone" className={inp} />
            <input value={block.email || ""} onChange={e => onChange({ email: e.target.value })} placeholder="Email" className={inp} />
            <input value={(block.openingHours || []).join(", ")} onChange={e => onChange({ openingHours: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="Hours (comma separated)" className={inp} />
          </div>
          <textarea value={block.description || ""} onChange={e => onChange({ description: e.target.value })} rows={2} placeholder="Description" className={inp} />
          <ImageInput value={block.image || ""} onChange={v => onChange({ image: v })} placeholder="Logo / photo" />
          <div className="grid grid-cols-3 gap-2">
            <input value={block.address?.streetAddress || ""} onChange={e => onChange({ address: { ...(block.address || {}), streetAddress: e.target.value } })} placeholder="Street" className={inp} />
            <input value={block.address?.addressLocality || ""} onChange={e => onChange({ address: { ...(block.address || {}), addressLocality: e.target.value } })} placeholder="City" className={inp} />
            <input value={block.address?.postalCode || ""} onChange={e => onChange({ address: { ...(block.address || {}), postalCode: e.target.value } })} placeholder="Postcode" className={inp} />
          </div>
        </div>
      );
    case "breadcrumb":
      return (
        <div className="space-y-2">
          {(block.items || []).map((it: any, idx: number) => (
            <div key={idx} className="flex gap-2">
              <input value={it.name} onChange={e => { const next = [...block.items]; next[idx] = { ...it, name: e.target.value }; onChange({ items: next }); }} placeholder="Name" className={inp} />
              <input value={it.url} onChange={e => { const next = [...block.items]; next[idx] = { ...it, url: e.target.value }; onChange({ items: next }); }} placeholder="URL" className={inp} />
              <button onClick={() => onChange({ items: block.items.filter((_: any, i: number) => i !== idx) })} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...(block.items || []), { name: "", url: "" }] })} className="text-sm text-primary font-medium">+ Add crumb</button>
        </div>
      );
    case "cta":
      return (
        <div className="space-y-2">
          <input value={block.title || ""} onChange={e => onChange({ title: e.target.value })} placeholder="Banner title" className={inp} />
          <textarea value={block.subtitle || ""} onChange={e => onChange({ subtitle: e.target.value })} rows={2} placeholder="Subtitle (optional)" className={inp} />
          <div className="grid grid-cols-2 gap-2">
            <input value={block.primaryLabel || ""} onChange={e => onChange({ primaryLabel: e.target.value })} placeholder="Primary button label" className={inp} />
            <input value={block.primaryHref || ""} onChange={e => onChange({ primaryHref: e.target.value })} placeholder="Primary link" className={inp} />
            <input value={block.secondaryLabel || ""} onChange={e => onChange({ secondaryLabel: e.target.value })} placeholder="Secondary button (optional)" className={inp} />
            <input value={block.secondaryHref || ""} onChange={e => onChange({ secondaryHref: e.target.value })} placeholder="Secondary link" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={block.background || "primary"} onChange={e => onChange({ background: e.target.value })} className={inp}>
              <option value="primary">Primary (brand color)</option>
              <option value="dark">Dark slate</option>
              <option value="light">Light grey</option>
              <option value="gradient">Brand gradient</option>
            </select>
            <ImageInput value={block.backgroundUrl || ""} onChange={v => onChange({ backgroundUrl: v })} placeholder="Background image (optional)" />
          </div>
        </div>
      );
    case "stats":
      return (
        <div className="space-y-2">
          {(block.items || []).map((it: any, idx: number) => (
            <div key={idx} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
              <input value={it.value} onChange={e => { const next = [...block.items]; next[idx] = { ...it, value: e.target.value }; onChange({ items: next }); }} placeholder="e.g. 15+" className={inp} />
              <input value={it.label} onChange={e => { const next = [...block.items]; next[idx] = { ...it, label: e.target.value }; onChange({ items: next }); }} placeholder="Label" className={inp} />
              <button onClick={() => onChange({ items: block.items.filter((_: any, i: number) => i !== idx) })} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => onChange({ items: [...(block.items || []), { value: "", label: "" }] })} className="text-sm text-primary font-medium">+ Add stat</button>
        </div>
      );
    case "featureGrid":
      return (
        <div className="space-y-2">
          <label className="text-xs text-slate-500 flex items-center gap-2">Columns: <input type="number" min={2} max={4} value={block.columns || 3} onChange={e => onChange({ columns: Number(e.target.value) })} className="w-20 px-2 py-1 border border-slate-200 rounded" /></label>
          {(block.items || []).map((it: any, idx: number) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-2 space-y-2">
              <div className="flex items-center gap-2">
                <input value={it.icon || ""} onChange={e => { const next = [...block.items]; next[idx] = { ...it, icon: e.target.value }; onChange({ items: next }); }} placeholder="Icon (emoji)" className="w-20 px-2 py-1.5 border border-slate-200 rounded text-center text-lg" />
                <input value={it.title || ""} onChange={e => { const next = [...block.items]; next[idx] = { ...it, title: e.target.value }; onChange({ items: next }); }} placeholder="Title" className={inp} />
                <button onClick={() => onChange({ items: block.items.filter((_: any, i: number) => i !== idx) })} className="p-2 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={it.description || ""} onChange={e => { const next = [...block.items]; next[idx] = { ...it, description: e.target.value }; onChange({ items: next }); }} rows={2} placeholder="Description" className={inp} />
            </div>
          ))}
          <button onClick={() => onChange({ items: [...(block.items || []), { icon: "✨", title: "", description: "" }] })} className="text-sm text-primary font-medium">+ Add feature</button>
        </div>
      );
    case "testimonial":
      return (
        <div className="space-y-2">
          <textarea value={block.text || ""} onChange={e => onChange({ text: e.target.value })} rows={3} placeholder="Quote text" className={inp} />
          <div className="grid grid-cols-2 gap-2">
            <input value={block.author || ""} onChange={e => onChange({ author: e.target.value })} placeholder="Author name" className={inp} />
            <input value={block.role || ""} onChange={e => onChange({ role: e.target.value })} placeholder="Role / location" className={inp} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex items-center gap-2">Rating: <input type="number" min={0} max={5} value={block.rating ?? 5} onChange={e => onChange({ rating: Number(e.target.value) })} className="w-20 px-2 py-1 border border-slate-200 rounded" /> / 5</label>
            <ImageInput value={block.avatarUrl || ""} onChange={v => onChange({ avatarUrl: v })} placeholder="Avatar (optional)" />
          </div>
        </div>
      );
    case "divider":
      return <div className="text-xs text-slate-400 text-center py-2">— Horizontal divider —</div>;
    case "spacer":
      return <label className="text-xs text-slate-500 flex items-center gap-2">Height: <input type="number" value={block.height || 32} onChange={e => onChange({ height: Number(e.target.value) })} className="w-24 px-2 py-1 border border-slate-200 rounded" /> px</label>;
    default:
      return null;
  }
}

function CollectionPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [opts, setOpts] = useState<{ slug: string; name: string }[]>([]);
  useEffect(() => { fetch(`${API}/admin/collections`, { credentials: "include" }).then(r => r.json()).then(setOpts).catch(() => setOpts([])); }, []);
  return (
    <select value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
      <option value="">— Select collection —</option>
      {opts.map(o => <option key={o.slug} value={o.slug}>{o.name} ({o.slug})</option>)}
    </select>
  );
}

const SCHEMA_TYPES = [
  { type: "localBusiness", label: "LocalBusiness", desc: "Tell Google your business name, address, hours, phone." },
  { type: "service", label: "Service", desc: "Mark this page as describing a specific service." },
  { type: "faq", label: "FAQ", desc: "Auto-builds from your FAQ blocks, or provide items manually." },
  { type: "breadcrumb", label: "Breadcrumb", desc: "Navigation trail for Google." },
  { type: "article", label: "Article", desc: "Blog post / article schema." },
];

export function SchemaPanel({ schemas, onChange }: { schemas: any[]; onChange: (s: any[]) => void }) {
  const inp = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg";
  const add = (type: string) => onChange([...schemas, { type, ...(type === "localBusiness" ? { name: "VertexAir Climate Systems", businessType: "HVACBusiness", telephone: "", email: "", address: { streetAddress: "", addressLocality: "", postalCode: "" }, openingHours: ["Mo-Fr 08:00-18:00"] } : type === "service" ? { name: "", serviceType: "", description: "", areaServed: "" } : type === "faq" ? { items: [] } : type === "breadcrumb" ? { items: [] } : type === "article" ? {} : {}) }]);
  const upd = (i: number, patch: any) => onChange(schemas.map((s, j) => j === i ? { ...s, ...patch } : s));
  const rem = (i: number) => onChange(schemas.filter((_, j) => j !== i));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 max-w-6xl">
      <div className="space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
          Structured data (JSON-LD) is auto-injected into <code>&lt;head&gt;</code> so Google can show rich results (stars, FAQ accordions, business info). You can use <code>{`{{variable.field}}`}</code> placeholders here for programmatic pages.
        </div>
        {schemas.length === 0 && <div className="text-center py-10 bg-white rounded-xl border border-dashed border-slate-300 text-slate-500 text-sm">No schemas yet. Add one from the right.</div>}
        {schemas.map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{s.type}</span>
              <button onClick={() => rem(i)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {s.type === "localBusiness" && (
              <div className="grid grid-cols-2 gap-2">
                <input value={s.businessType || "LocalBusiness"} onChange={e => upd(i, { businessType: e.target.value })} placeholder="Type (e.g. HVACBusiness)" className={inp} />
                <input value={s.name || ""} onChange={e => upd(i, { name: e.target.value })} placeholder="Name" className={inp} />
                <input value={s.telephone || ""} onChange={e => upd(i, { telephone: e.target.value })} placeholder="Phone" className={inp} />
                <input value={s.email || ""} onChange={e => upd(i, { email: e.target.value })} placeholder="Email" className={inp} />
                <input value={s.priceRange || ""} onChange={e => upd(i, { priceRange: e.target.value })} placeholder="Price range ($-$$$$)" className={inp} />
                <input value={s.areaServed || ""} onChange={e => upd(i, { areaServed: e.target.value })} placeholder="Area served" className={inp} />
                <input value={s.address?.streetAddress || ""} onChange={e => upd(i, { address: { ...(s.address || {}), streetAddress: e.target.value } })} placeholder="Street" className={inp} />
                <input value={s.address?.addressLocality || ""} onChange={e => upd(i, { address: { ...(s.address || {}), addressLocality: e.target.value } })} placeholder="City" className={inp} />
                <input value={s.address?.postalCode || ""} onChange={e => upd(i, { address: { ...(s.address || {}), postalCode: e.target.value } })} placeholder="Postcode" className={inp} />
                <input value={(s.openingHours || []).join(", ")} onChange={e => upd(i, { openingHours: e.target.value.split(",").map((x: string) => x.trim()).filter(Boolean) })} placeholder="Hours: Mo-Fr 08:00-18:00" className={inp} />
              </div>
            )}
            {s.type === "service" && (
              <div className="grid grid-cols-2 gap-2">
                <input value={s.name || ""} onChange={e => upd(i, { name: e.target.value })} placeholder="Service name" className={inp} />
                <input value={s.serviceType || ""} onChange={e => upd(i, { serviceType: e.target.value })} placeholder="Service type" className={inp} />
                <input value={s.areaServed || ""} onChange={e => upd(i, { areaServed: e.target.value })} placeholder="Area served" className={inp} />
                <input value={s.providerName || ""} onChange={e => upd(i, { providerName: e.target.value })} placeholder="Provider name" className={inp} />
                <textarea value={s.description || ""} onChange={e => upd(i, { description: e.target.value })} rows={2} placeholder="Description" className={`${inp} col-span-2`} />
              </div>
            )}
            {s.type === "faq" && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">By default we auto-collect questions from your FAQ content blocks. You can also add items manually here:</p>
                {(s.items || []).map((it: any, k: number) => (
                  <div key={k} className="flex gap-2">
                    <input value={it.question} onChange={e => { const next = [...(s.items || [])]; next[k] = { ...it, question: e.target.value }; upd(i, { items: next }); }} placeholder="Question" className={inp} />
                    <input value={it.answer} onChange={e => { const next = [...(s.items || [])]; next[k] = { ...it, answer: e.target.value }; upd(i, { items: next }); }} placeholder="Answer" className={inp} />
                    <button onClick={() => upd(i, { items: (s.items || []).filter((_: any, j: number) => j !== k) })} className="p-2 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => upd(i, { items: [...(s.items || []), { question: "", answer: "" }] })} className="text-sm text-primary font-medium">+ Add</button>
              </div>
            )}
            {s.type === "breadcrumb" && (
              <div className="space-y-2">
                {(s.items || []).map((it: any, k: number) => (
                  <div key={k} className="flex gap-2">
                    <input value={it.name} onChange={e => { const next = [...(s.items || [])]; next[k] = { ...it, name: e.target.value }; upd(i, { items: next }); }} placeholder="Name" className={inp} />
                    <input value={it.url} onChange={e => { const next = [...(s.items || [])]; next[k] = { ...it, url: e.target.value }; upd(i, { items: next }); }} placeholder="URL" className={inp} />
                    <button onClick={() => upd(i, { items: (s.items || []).filter((_: any, j: number) => j !== k) })} className="p-2 text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => upd(i, { items: [...(s.items || []), { name: "", url: "" }] })} className="text-sm text-primary font-medium">+ Add crumb</button>
                <p className="text-xs text-slate-400">Leave empty to auto-generate Home → This Page.</p>
              </div>
            )}
            {s.type === "article" && <p className="text-xs text-slate-500">Article schema auto-fills from the page's SEO title, meta description, and featured image.</p>}
          </div>
        ))}
      </div>
      <div className="lg:sticky lg:top-32 lg:self-start space-y-2">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Add Schema</h3>
          <div className="space-y-1.5">
            {SCHEMA_TYPES.map(t => (
              <button key={t.type} onClick={() => add(t.type)} className="w-full text-left p-2.5 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 text-slate-700 hover:text-primary transition">
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-xs text-slate-400">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function RichTextarea({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={4} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg font-mono text-xs" placeholder="HTML allowed: <b>, <i>, <a>, <br>, <ul>, <li>..." />
      <p className="text-xs text-slate-400">Tip: use &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;a href="..."&gt;link&lt;/a&gt;</p>
    </div>
  );
}

export function SeoPanel({ page, onChange }: { page: Page; onChange: (p: Partial<Page>) => void }) {
  const [previewMode, setPreviewMode] = useState<"mobile" | "desktop">("desktop");
  const seoTitle = page.seoTitle || page.title;
  const metaDesc = page.metaDescription || "";
  const slug = page.slug;
  const titleLen = seoTitle.length;
  const descLen = metaDesc.length;
  const titleScore = titleLen >= 30 && titleLen <= 60 ? "good" : titleLen > 60 ? "long" : "short";
  const descScore = descLen >= 120 && descLen <= 160 ? "good" : descLen > 160 ? "long" : descLen === 0 ? "missing" : "short";

  const analysis = useMemo(() => analyzeSeo(page), [page]);

  const inp = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Search className="w-4 h-4 text-slate-500" /> Search Appearance</h3>
            <div className="flex bg-slate-100 rounded-lg p-0.5 text-xs">
              <button onClick={() => setPreviewMode("mobile")} className={`px-3 py-1 rounded font-medium flex items-center gap-1 ${previewMode === "mobile" ? "bg-white shadow-sm" : "text-slate-500"}`}><Smartphone className="w-3 h-3" /> Mobile</button>
              <button onClick={() => setPreviewMode("desktop")} className={`px-3 py-1 rounded font-medium flex items-center gap-1 ${previewMode === "desktop" ? "bg-white shadow-sm" : "text-slate-500"}`}><Monitor className="w-3 h-3" /> Desktop</button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mb-4">How this page will appear in Google search results.</p>
          <div className={`border border-slate-200 rounded-xl p-4 ${previewMode === "mobile" ? "max-w-sm" : ""}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">V</div>
              <div>
                <div className="text-xs font-medium text-slate-700 leading-tight">VertexAir Climate Systems</div>
                <div className="text-xs text-slate-500">{new URL(SITE_ORIGIN).host}{slug ? ` › ${slug.split("-").join(" ")}` : ""}</div>
              </div>
            </div>
            <a className={`text-blue-700 hover:underline font-medium ${previewMode === "mobile" ? "text-lg leading-snug" : "text-xl leading-snug"} block mt-2 line-clamp-2`} href="#">{seoTitle || "Page title"}</a>
            <p className="text-sm text-slate-600 mt-1 line-clamp-3">{metaDesc || "Add a meta description to control how this page appears in search results."}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">SEO Title</label>
            <input value={page.seoTitle || ""} onChange={e => onChange({ seoTitle: e.target.value })} placeholder={page.title} className={inp} />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className="text-slate-400">{titleLen} / 60 chars</span>
              <div className={`w-full max-w-xs ml-2 h-1.5 rounded-full ${titleScore === "good" ? "bg-emerald-100" : titleScore === "long" ? "bg-rose-100" : "bg-amber-100"}`}>
                <div className={`h-full rounded-full ${titleScore === "good" ? "bg-emerald-500" : titleScore === "long" ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${Math.min(100, (titleLen / 60) * 100)}%` }} />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Slug (URL)</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400 whitespace-nowrap">{SITE_ORIGIN.replace(/^https?:\/\//, "")}/</span>
              <input value={page.slug} onChange={e => onChange({ slug: slugify(e.target.value) })} className={inp} />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Meta Description</label>
            <textarea value={page.metaDescription || ""} onChange={e => onChange({ metaDescription: e.target.value })} rows={3} placeholder="Write 120–160 characters describing this page for search results." className={inp} />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              <span className="text-slate-400">{descLen} / 160 chars</span>
              <div className={`w-full max-w-xs ml-2 h-1.5 rounded-full ${descScore === "good" ? "bg-emerald-100" : descScore === "long" || descScore === "missing" ? "bg-rose-100" : "bg-amber-100"}`}>
                <div className={`h-full rounded-full ${descScore === "good" ? "bg-emerald-500" : descScore === "long" || descScore === "missing" ? "bg-rose-500" : "bg-amber-500"}`} style={{ width: `${Math.min(100, (descLen / 160) * 100)}%` }} />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block flex items-center gap-1"><Tag className="w-3 h-3" /> Focus Keyphrase</label>
            <input value={page.focusKeyphrase || ""} onChange={e => onChange({ focusKeyphrase: e.target.value })} placeholder="e.g. air conditioning installation london" className={inp} />
            <p className="text-xs text-slate-400 mt-1">The main keyphrase you want this page to rank for. We'll check that it appears in your title, URL, content, and meta description.</p>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Related Keywords</label>
            <KeywordsInput value={page.keywords || []} onChange={kw => onChange({ keywords: kw })} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Featured Image (for Google search & social shares)</label>
            <ImageInput value={page.featuredImageUrl || ""} onChange={v => onChange({ featuredImageUrl: v, ogImageUrl: page.ogImageUrl || v })} />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Canonical URL (optional)</label>
            <input value={page.canonicalUrl || ""} onChange={e => onChange({ canonicalUrl: e.target.value })} placeholder="https://www.example.com/canonical-page" className={inp} />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={page.noindex} onChange={e => onChange({ noindex: e.target.checked })} />
            <span>Hide from search engines (noindex)</span>
          </label>
        </div>
      </div>

      <div className="lg:sticky lg:top-32 lg:self-start space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
            SEO Analysis
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${analysis.grade === "Excellent" ? "bg-emerald-100 text-emerald-700" : analysis.grade === "Good" ? "bg-blue-100 text-blue-700" : analysis.grade === "Fair" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>{analysis.grade}</span>
          </h3>
          <p className="text-xs text-slate-500 mb-3">Score {analysis.score}/100</p>
          <div className="space-y-2">
            {analysis.checks.map((c, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                {c.ok ? <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> : <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${c.severity === "error" ? "text-rose-500" : "text-amber-500"}`} />}
                <div>
                  <p className={`${c.ok ? "text-slate-700" : "text-slate-900"} font-medium`}>{c.title}</p>
                  {c.detail && <p className="text-xs text-slate-500">{c.detail}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KeywordsInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim(); if (v && !value.includes(v)) onChange([...value, v]); setText(""); };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((k, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-xs text-slate-700">
            {k} <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500">×</button>
          </span>
        ))}
      </div>
      <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add} placeholder="Type and press Enter" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" />
    </div>
  );
}

type Check = { title: string; ok: boolean; severity: "warn" | "error"; detail?: string };

function analyzeSeo(page: Page): { score: number; grade: "Excellent" | "Good" | "Fair" | "Poor"; checks: Check[] } {
  const checks: Check[] = [];
  const kp = (page.focusKeyphrase || "").toLowerCase().trim();
  const title = (page.seoTitle || page.title || "").toLowerCase();
  const meta = (page.metaDescription || "").toLowerCase();
  const slug = (page.slug || "").toLowerCase();
  const bodyText = (page.blocks || []).map(b => (b.text || b.html || b.title || b.subtitle || b.label || "")).join(" ").replace(/<[^>]*>/g, " ").toLowerCase();
  const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
  const imgCount = (page.blocks || []).filter(b => b.type === "image" || b.type === "hero" || b.type === "gallery").length;
  const imgsWithAlt = (page.blocks || []).filter(b => b.type === "image" && b.alt).length;
  const imgsTotal = (page.blocks || []).filter(b => b.type === "image").length;

  checks.push({ title: "Focus keyphrase set", ok: !!kp, severity: "error", detail: kp ? undefined : "Pick a focus keyphrase to enable SEO checks." });
  if (kp) {
    checks.push({ title: "Keyphrase in SEO title", ok: title.includes(kp), severity: "warn", detail: title.includes(kp) ? undefined : "Include your focus keyphrase in the SEO title." });
    checks.push({ title: "Keyphrase in URL slug", ok: slug.split("-").join(" ").includes(kp) || slug.includes(kp.replace(/\s+/g, "-")), severity: "warn", detail: "Use the keyphrase in the URL slug." });
    checks.push({ title: "Keyphrase in meta description", ok: meta.includes(kp), severity: "warn", detail: "Mention the keyphrase in the meta description." });
    checks.push({ title: "Keyphrase in content", ok: bodyText.includes(kp), severity: "error", detail: "Use the keyphrase in your page content." });
  }
  const titleLen = (page.seoTitle || page.title || "").length;
  checks.push({ title: `SEO title length (${titleLen}/60)`, ok: titleLen >= 30 && titleLen <= 60, severity: "warn", detail: titleLen > 60 ? "Title may be truncated in search results." : titleLen < 30 ? "Title is short — aim for 30–60 chars." : undefined });
  const descLen = (page.metaDescription || "").length;
  checks.push({ title: `Meta description length (${descLen}/160)`, ok: descLen >= 120 && descLen <= 160, severity: descLen === 0 ? "error" : "warn", detail: descLen === 0 ? "Add a meta description (120–160 chars)." : descLen > 160 ? "Description may be truncated." : descLen < 120 ? "Description is short — aim for 120–160 chars." : undefined });
  checks.push({ title: `Content length (${wordCount} words)`, ok: wordCount >= 300, severity: "warn", detail: wordCount < 300 ? "Aim for at least 300 words for better SEO." : undefined });
  checks.push({ title: "Featured image set", ok: !!page.featuredImageUrl, severity: "warn", detail: page.featuredImageUrl ? undefined : "Add a featured image for Google and social shares." });
  if (imgsTotal > 0) checks.push({ title: `Images have alt text (${imgsWithAlt}/${imgsTotal})`, ok: imgsWithAlt === imgsTotal, severity: "warn", detail: imgsWithAlt < imgsTotal ? "Add alt text to every image." : undefined });
  if (imgCount === 0 && wordCount > 100) checks.push({ title: "Page has imagery", ok: false, severity: "warn", detail: "Pages with images tend to rank better." });

  const errors = checks.filter(c => !c.ok && c.severity === "error").length;
  const warns = checks.filter(c => !c.ok && c.severity === "warn").length;
  const total = checks.length;
  const score = Math.max(0, Math.round(((total - errors * 2 - warns) / total) * 100));
  const grade: "Excellent" | "Good" | "Fair" | "Poor" =
    score >= 85 ? "Excellent" : score >= 70 ? "Good" : score >= 50 ? "Fair" : "Poor";
  return { score, grade, checks };
}

function SettingsPanel({ page, parentOptions, onChange }: { page: Page; parentOptions: Page[]; onChange: (p: Partial<Page>) => void }) {
  const inp = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg";
  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Parent Page</label>
          <select value={page.parentId ?? ""} onChange={e => onChange({ parentId: e.target.value ? Number(e.target.value) : null })} className={inp}>
            <option value="">— None (top-level page) —</option>
            {parentOptions.filter(p => !p.parentId).map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">A child page's URL will be /parent-slug/this-slug.</p>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={page.showInNav} onChange={e => onChange({ showInNav: e.target.checked })} /> Show in main navigation</label>
          <div className="flex items-center gap-2 text-sm text-slate-500">Order: <input type="number" value={page.navOrder} onChange={e => onChange({ navOrder: Number(e.target.value) || 0 })} className="w-20 px-2 py-1 border border-slate-200 rounded" /></div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Status</label>
          <select value={page.status} onChange={e => onChange({ status: e.target.value as any })} className={inp}>
            <option value="draft">Draft (not visible to visitors)</option>
            <option value="published">Published (live on the site)</option>
          </select>
          {page.publishedAt && <p className="text-xs text-slate-400 mt-1">First published {new Date(page.publishedAt).toLocaleString()}</p>}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        <p className="font-semibold mb-1">Tips</p>
        <ul className="list-disc pl-5 space-y-1 text-amber-800">
          <li>Header and footer come from your site — they're not part of the builder.</li>
          <li>Drag blocks by their header to reorder, or use the ↑↓ buttons.</li>
          <li>Use the SEO tab to control how this page appears in Google.</li>
        </ul>
      </div>
    </div>
  );
}

function PreviewModal({ page, onClose }: { page: Page; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">{page.title}</h3>
            <p className="text-xs text-slate-500">/{page.slug}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">✕</button>
        </div>
        <div className="overflow-auto p-6 bg-slate-50">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">{page.title}</h1>
            <PreviewBlocks blocks={page.blocks} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewBlocks({ blocks }: { blocks: Block[] }) {
  return (
    <div className="space-y-4 text-slate-700">
      {(blocks || []).map((b, i) => {
        switch (b.type) {
          case "heading": {
            const sizes = ["", "text-3xl", "text-2xl", "text-xl", "text-lg"];
            return <div key={i} className={`font-bold ${sizes[b.level || 2]} text-${b.align || "left"}`}>{b.text}</div>;
          }
          case "paragraph": return <div key={i} className={`text-${b.align || "left"}`} dangerouslySetInnerHTML={{ __html: b.html || "" }} />;
          case "image": return b.url ? <div key={i} className={`text-${b.align || "center"}`}><img src={b.url} alt={b.alt || ""} className="max-w-full rounded inline-block" /></div> : null;
          case "button": return <div key={i} className={`text-${b.align || "left"}`}><a href={b.href} className="inline-block bg-primary text-white px-5 py-2 rounded-lg font-medium">{b.label}</a></div>;
          case "divider": return <hr key={i} />;
          case "spacer": return <div key={i} style={{ height: b.height || 32 }} />;
          case "hero": return <div key={i} className="rounded-xl p-10 text-white text-center" style={{ backgroundImage: b.backgroundUrl ? `linear-gradient(rgba(15,47,79,${b.overlay ?? 0.45}),rgba(15,47,79,${b.overlay ?? 0.45})), url(${b.backgroundUrl})` : "none", backgroundColor: "#0F2F4F", backgroundSize: "cover" }}><h2 className="text-3xl font-bold mb-2">{b.title}</h2>{b.subtitle && <p>{b.subtitle}</p>}{b.ctaLabel && <a href={b.ctaHref} className="inline-block mt-4 bg-white text-primary px-5 py-2 rounded-lg font-medium">{b.ctaLabel}</a>}</div>;
          case "twoColumn": return <div key={i} className="grid grid-cols-2 gap-6">
            <div className="space-y-3">{b.leftHtml && <div dangerouslySetInnerHTML={{ __html: b.leftHtml }} />}{Array.isArray(b.leftBlocks) && b.leftBlocks.length > 0 && <PreviewBlocks blocks={b.leftBlocks} />}</div>
            <div className="space-y-3">{b.rightHtml && <div dangerouslySetInnerHTML={{ __html: b.rightHtml }} />}{Array.isArray(b.rightBlocks) && b.rightBlocks.length > 0 && <PreviewBlocks blocks={b.rightBlocks} />}</div>
          </div>;
          case "columns": { const c = Math.max(2, Math.min(6, Number(b.columns) || 3)); return <div key={i} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${c}, minmax(0, 1fr))`, gap: `${b.gap ?? 24}px` }}>{(b.items || []).slice(0, c).map((it: any, j: number) => <div key={j} className="space-y-3">{it?.html && <div dangerouslySetInnerHTML={{ __html: it.html }} />}{Array.isArray(it?.blocks) && it.blocks.length > 0 && <PreviewBlocks blocks={it.blocks} />}</div>)}</div>; }
          case "video": return <div key={i} className="bg-slate-100 rounded p-6 text-center text-slate-500">Video: {b.url}</div>;
          case "gallery": return <div key={i} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${b.columns || 3}, 1fr)` }}>{(b.images || []).map((im: any, j: number) => <img key={j} src={im.url} alt={im.alt} className="w-full aspect-square object-cover rounded" />)}</div>;
          case "list": { const T = (b.ordered ? "ol" : "ul") as any; return <T key={i} className={`${b.ordered ? "list-decimal" : "list-disc"} pl-6 space-y-1`}>{(b.items || []).map((it: string, j: number) => <li key={j}>{it}</li>)}</T>; }
          case "quote": return <blockquote key={i} className="border-l-4 border-primary pl-4 italic">"{b.text}"{b.author && <footer className="text-sm mt-1 not-italic">— {b.author}</footer>}</blockquote>;
          case "html": return <div key={i} dangerouslySetInnerHTML={{ __html: b.html || "" }} />;
          default: return null;
        }
      })}
    </div>
  );
}
